import { Card } from '../../components/ui/Card';
import type { Room, RoomView } from './api';

/**
 * The session numbers (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## Scoped to this broadcast, not to today
 *
 * A host who ran a room this morning and another tonight wants tonight's
 * numbers, and "today" is the wrong unit for anybody broadcasting across
 * midnight. The server scopes every figure here to `live_started_at`.
 *
 * ## What the design shows that this does not
 *
 * The mockup has "avg listen time" and "follows gained by artists". Both are
 * real and computable and simply are not wired yet.
 *
 * The payout panel is absent because boosts carry no money.
 */
export function SessionPanel({ room, view }: { room: Room; view: RoomView }) {
  const { session } = view;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">Session</span>
        {room.live_started_at && (
          <span className="font-mono text-[10px] text-ghost">
            since{' '}
            {new Date(room.live_started_at.replace(' ', 'T') + 'Z').toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <Figure value={room.listener_count} label="listening now" />
        <Figure value={room.peak_listeners} label="peak listeners" />
        <Figure value={session.tracks_played} label="tracks played" tone="text-accent" />
        <Figure value={session.artists_played} label="artists played" tone="text-fresh" />
      </div>

      {/*
        Above the curve, because they answer different questions and one of them
        is time-critical. The curve is context - which way the room is going -
        and a host can read it whenever. The loudest minute is an event: the
        thing to clip, the track to play again, the artist to remember. Below
        the curve it sat under the fold of a pane that is exactly one screen
        tall, which for the one number a host cannot get anywhere else is the
        wrong place to put it.
      */}
      <Hype hype={view.hype} />

      <Curve points={view.curve ?? []} />

      <dl className="flex flex-col gap-2 pt-1">
        <Row label="in the queue" value={String(session.queued)} />
        <Row label="boosted" value={String(session.boosted)} tone="text-accent" />
        {/* The single most useful number a host has about their own taste, which
            is why it is here rather than hidden in an analytics page. */}
        <Row
          label="skipped early"
          value={String(session.skipped_early)}
          tone={session.skipped_early > 0 ? 'text-hot-text' : undefined}
        />
      </dl>
    </section>
  );
}

/**
 * Listeners over the last ninety minutes.
 *
 * The one figure on this panel that answers a question a number cannot: not
 * "how many" but "which way". A host watching thirty listeners does not know
 * whether that is the top of a climb or the bottom of a fall, and the whole
 * value of the panel during a broadcast is telling those apart.
 *
 * `stations-sweep.php` writes a sample a minute, so this is empty for the first
 * few minutes of a broadcast and says so. Drawing a plausible shape over no
 * data would be worse than drawing nothing, because it would be believed.
 */
function Curve({ points }: { points: { at: string; listeners: number }[] }) {
  if (points.length < 2) {
    return (
      <Card className="flex flex-col gap-2 p-4">
        <span className="meta-caps text-ghost">Listeners · last 90 min</span>
        <p className="font-mono text-[10px] text-faint">
          A few minutes in, this fills up.
        </p>
      </Card>
    );
  }

  /*
   * Scaled to the window's own peak, not to `peak_listeners`, which is the
   * whole broadcast's. A room that had two hundred an hour ago and forty now
   * would otherwise draw ninety minutes of flat baseline.
   *
   * Floored at 1 so a stretch of zero listeners divides by something.
   */
  const peak = Math.max(1, ...points.map((point) => point.listeners));
  const last = points.length - 1;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <span className="meta-caps text-ghost">Listeners · last 90 min</span>

      <div className="flex h-12 items-end gap-[3px]" data-listener-curve>
        {points.map((point, index) => (
          <span
            key={point.at}
            title={`${point.listeners} at ${time(point.at)}`}
            // The newest bar in the accent: it is the number the host is
            // watching, and it is the one the rest of the shape is context for.
            className={`min-w-px flex-1 rounded-[2px] ${index === last ? 'bg-accent' : 'bg-track'}`}
            style={{ height: `${Math.max(4, (point.listeners / peak) * 100)}%` }}
          />
        ))}
      </div>

      <div className="flex justify-between font-mono text-[9px] text-ghost">
        <span>{time(points[0]!.at)}</span>
        <span>now</span>
      </div>
    </Card>
  );
}

/**
 * What the room reacted to, which is the half of a broadcast a host cannot see.
 *
 * ## Why this is on the console at all
 *
 * The meter is on the stage, and the stage is the page the *audience* has open.
 * A host is here: mixing, reading a queue, watching chat. They can be having
 * the best twenty minutes of their year and have no idea, which is the same
 * argument that put reactions in the transcript - except a number climbing is
 * not a chat line, so this is where it goes.
 *
 * ## The loudest minute is the whole point
 *
 * A total is a fact about the night. "The room went off at 21:43, during this
 * track" is a thing a host can act on - it is the moment they will clip, the
 * track they will play again, and the artist they will remember. It exists
 * nowhere else, because nobody was watching the meter at 21:43 except the room.
 *
 * ## Late on purpose
 *
 * The counter lives in Redis and the sweep files it a minute at a time, so
 * these numbers trail the stage by up to a minute. That is the right trade -
 * the alternative is a table with a row per tap - and it is why the total here
 * can read lower than the meter a listener is looking at.
 */
function Hype({ hype }: { hype: RoomView['hype'] }) {
  // Nothing yet: either nobody has tapped, or the first minute has not been
  // filed. Silence rather than a row of zeroes, like the curve above.
  if (!hype || hype.total === 0) return null;

  return (
    <Card className="flex flex-col gap-3 p-4" data-console-hype>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-ghost">Hype</span>
        <span className="text-xl font-bold tracking-tight text-hot-text" data-console-hype-total>
          {hype.total.toLocaleString()}
        </span>
      </div>

      {hype.peak && (
        <div className="flex flex-col gap-0.5" data-console-hype-peak>
          <span className="font-mono text-[10px] text-faint">
            loudest at {time(hype.peak.at)} · {hype.peak.taps.toLocaleString()} in a minute
          </span>
          {/* Null when the room was between tracks, which is a real answer -
              somebody cheering into a gap is cheering at the room. */}
          <span className="truncate text-[13px] text-ink">
            {hype.peak.track
              ? `${hype.peak.track.title} — ${hype.peak.track.artist}`
              : 'between tracks'}
          </span>
        </div>
      )}

      {hype.tracks.length > 0 && (
        <dl className="flex flex-col gap-1.5 border-t border-line pt-2.5">
          {hype.tracks.map((track) => (
            <div key={track.id} className="flex items-center justify-between gap-3 font-mono text-[11px]">
              <dt className="truncate text-faint">
                {track.title} <span className="text-ghost">· {track.artist}</span>
              </dt>
              <dd className="shrink-0 text-ink">{track.taps.toLocaleString()}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

/** `22:04` from the server's `Y-m-d H:i:s`, which is UTC. */
function time(at: string): string {
  return new Date(at.replace(' ', 'T') + 'Z').toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Figure({ value, label, tone = 'text-ink' }: { value: number; label: string; tone?: string }) {
  return (
    <Card className="flex flex-col gap-1 p-3.5">
      <span className={`text-xl font-bold tracking-tight ${tone}`}>{value.toLocaleString()}</span>
      <span className="font-mono text-[9px] text-faint">{label}</span>
    </Card>
  );
}

function Row({ label, value, tone = 'text-ink' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 font-mono text-[11px]">
      <dt className="truncate text-faint">{label}</dt>
      <dd className={`shrink-0 ${tone}`}>{value}</dd>
    </div>
  );
}
